import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AlarmClockPlusIcon,
  BanIcon,
  FileDigitIcon,
  FileDownIcon,
  RotateCcwClockIcon,
  Trash2Icon,
  Undo2Icon,
} from "lucide-react";
import style from "../../OrderInfoAviation.module.scss";
import CloseIcon from "../../../../shared/icons/CloseIcon";
import CommentIcon from "../../../../shared/icons/CommentIcon";
import EditIconNew from "../../../../shared/icons/EditIconNew";
import LocationIconNew from "../../../../shared/icons/LocationIconNew";
import MoreIcon from "../../../../shared/icons/MoreIcon";
import PlusIcon from "../../../../shared/icons/PlusIcon";
import CancelApp from "../../../../order/info/CancelApp";
import OrdersDelete from "../../../../order/info/OrdersDelete";
import ExtendTenderEndDate from "../../../../orders-new/order-info/components/ExtendTenderEndDate/ExtendTenderEndDate";
import AddProjectNumberModal from "../../../../orders-new/order-info/components/AddProjectNumberModal/AddProjectNumberModal";
import EditAviationRouteDatesModal from "./EditAviationRouteDatesModal";
import RebidTenderModal from "../../../../orders-new/order-info-client/components/RebidTenderModal";
import ReturnToTenderModal from "../../../../orders-new/order-info-client/components/ReturnToTenderModal";
import { Aviation, File } from "../../../../../services";
import { CURRENCIES } from "../../../../../mock";
import { numberToFinanceFormat } from "../../../../../utils";
import { toast } from "react-toastify";
import AppTooltip from "../../../../shared/AppTooltip/AppTooltip";

export const AVIATION_DATE_EDIT_STATUSES = ["confirmed", "accepted"];

const OrderInfoAviationHeader = ({
  item,
  setIsOpen,
  onOpenChat,
  unreadCount = 0,
  onForceUpdate,
  hasBids = false,
  onOrderDataChanged = onForceUpdate,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(item?.tenderDateEnd, { inclusive: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state);
  const moreRef = useRef(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isExtendTenderOpen, setIsExtendTenderOpen] = useState(false);
  const [isProjectNumberOpen, setIsProjectNumberOpen] = useState(false);
  const [isEditRouteDatesOpen, setIsEditRouteDatesOpen] = useState(false);
  const [isRebidTenderOpen, setIsRebidTenderOpen] = useState(false);
  const [isReturnToTenderOpen, setIsReturnToTenderOpen] = useState(false);

  const isOwner = user?.companyId === item?.companyId;
  const isTenderExpired = item?.tenderDateEnd ? isTenderExpiredByServer : false;
  const shouldShowRebidButton = isOwner && item?.status === "awaitingoffer" && isTenderExpired && hasBids;
  const shouldShowReturnToTenderButton =
    isOwner &&
    (item?.isTender || ["Tender", "Auction"].includes(item?.assignmentMethod)) &&
    item?.assignmentMethod !== "DirectAssignment" &&
    item?.parentId == null &&
    ["confirmed", "accepted"].includes(item?.status);
  const currencySymbol = CURRENCIES[item?.currency || "KZT"]?.symbol || "";
  const routeTitle = item ? `${item.cityFromName || ""} - ${item.cityToName || ""}` : "";
  const subtitleSuffix = item?.expeditorName || "";
  const subtitleMain = item
    ? `№ ${item.number || ""}${item.referenceNumber ? ` - ${item.referenceNumber}` : ""}`
    : "";

  const canModifyOrder =
    item?.status === "awaitingoffer" ||
    item?.status === "moderation" ||
    item?.status === "awaiting_payment" ||
    String(item?.status || "").toLowerCase() === "expeditorrejected" ||
    item?.status === "canceled";
  const isDateOnlyEditStatus = AVIATION_DATE_EDIT_STATUSES.includes(item?.status);

  useEffect(() => {
    if (!isMoreOpen) return undefined;

    const handleClickOutside = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMoreOpen]);

  const downloadOrderPdf = async () => {
    try {
      const response = await File.getAirOrderPdf(item.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `order_${item.number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsMoreOpen(false);
    }
  };

  return (
    <div className={style.header}>
      <div className={style.header_left}>
        <CloseIcon
          fill="#6D7885"
          width="24"
          height="24"
          onClick={() => (setIsOpen ? setIsOpen(false) : navigate(-1))}
          style={{ cursor: "pointer", flexShrink: 0 }}
        />
        <div className={style.header_info}>
          <div className={style.header_title}>{routeTitle}</div>
          <div className={style.header_subtitle}>
            <span className={style.header_subtitle_main}>{subtitleMain}</span>
            {subtitleSuffix ? (
              <>
                <span className={style.header_subtitle_sep}>•</span>
                {item?.expeditorName ? (
                  <>
                    <span
                      className={style.header_subtitle_main}
                      data-tooltip-id="aviation-header-expeditor-tooltip"
                    >
                      {`${t("orderInfoExpeditor.header.partner")}:`}
                    </span>
                    <span
                      className={style.header_subtitle_expeditor}
                      data-tooltip-id="aviation-header-expeditor-tooltip"
                    >
                      {item.expeditorName}
                    </span>
                    <AppTooltip
                      id="aviation-header-expeditor-tooltip"
                      positionStrategy="fixed"
                      delayShow={50}
                    >
                      {item.expeditorName}
                    </AppTooltip>
                  </>
                ) : (
                  <span className={style.header_subtitle_main}>{subtitleSuffix}</span>
                )}
              </>
            ) : null}
          </div>
        </div>
        {item?.price ? (
          <div className={style.header_price}>
            {numberToFinanceFormat(item.price, true)} {currencySymbol}
          </div>
        ) : null}
      </div>

      <div className={style.header_right}>
        <div className={style.header_badge_wrapper}>
          <button type="button" className={style.header_action_btn} onClick={onOpenChat}>
            <CommentIcon width={20} height={20} fill="#0D3B66" type={2} />
            <span>{t("orderInfo.ordersInfoBasic.chatButton")}</span>
          </button>
          {unreadCount > 0 ? <span className={style.header_badge}>{unreadCount}</span> : null}
        </div>

        {user?.type === "Client" ? (
          <div className={style.more_wrapper} ref={moreRef}>
            <button
              type="button"
              className={style.more_trigger}
              aria-label={t("tooltips.additionalMenu")}
              onClick={() => setIsMoreOpen((value) => !value)}
            >
              <MoreIcon width={24} height={24} />
            </button>
            <div className={cn(style.more_dropdown, { [style.more_dropdown_open]: isMoreOpen })}>
              {(canModifyOrder || isDateOnlyEditStatus) && isOwner ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    if (isDateOnlyEditStatus) {
                      setIsEditRouteDatesOpen(true);
                      setIsMoreOpen(false);
                      return;
                    }

                    navigate(`/aviation/edit/${item.id}`);
                    setIsMoreOpen(false);
                  }}
                >
                  <EditIconNew width={18} height={18} />
                  {t(
                    isDateOnlyEditStatus
                      ? "orderInfoClient.moreMenu.editOrderDate"
                      : "tooltips.editOrder",
                  )}
                </button>
              ) : null}
              {isOwner ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    navigate(`/aviation/copy/${item.id}`);
                    setIsMoreOpen(false);
                  }}
                >
                  <PlusIcon width={18} height={18} fill="#6D7885" />
                  {t("orderInfo.ordersInfoHeader.copyButton")}
                </button>
              ) : null}
              {isOwner && item?.status === "awaitingoffer" && item?.tenderDateEnd ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    setIsExtendTenderOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <AlarmClockPlusIcon size={18} color="#6D7885" />
                  {t("orderInfo.ordersInfoHeader.extendTenderAction")}
                </button>
              ) : null}
              {shouldShowRebidButton ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    setIsRebidTenderOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <RotateCcwClockIcon size={18} color="#6D7885" />
                  {t("orderInfoClient.rebid.action")}
                </button>
              ) : null}
              {isOwner ? (
                <button type="button" className={style.more_dropdown_item} onClick={downloadOrderPdf}>
                  <FileDownIcon size={18} color="#6D7885" />
                  {t("orderInfo.ordersInfoHeader.generateOrderPdf")}
                </button>
              ) : null}
              {isOwner ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    setIsProjectNumberOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <FileDigitIcon size={18} color="#6D7885" />
                  {t("orderInfo.ordersInfoHeader.addProjectNumber")}
                </button>
              ) : null}
              {shouldShowReturnToTenderButton ? (
                <button
                  type="button"
                  className={style.more_dropdown_item}
                  onClick={() => {
                    setIsReturnToTenderOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <Undo2Icon size={18} color="#6D7885" />
                  {t(
                    item?.assignmentMethod === "Auction"
                      ? "orderInfoClient.moreMenu.returnToAuction"
                      : "orderInfoClient.moreMenu.returnToTender",
                  )}
                </button>
              ) : null}
              {(canModifyOrder || ["confirmed", "assigned"].includes(item?.status)) &&
              isOwner &&
              item?.status !== "canceled" ? (
                <button
                  type="button"
                  className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
                  onClick={() => {
                    setIsCancelOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <BanIcon size={18} color="#E53935" />
                  {t("orderInfo.ordersInfoHeader.withdrawOrderButton")}
                </button>
              ) : null}
              {/* {(canModifyOrder || item?.status === "confirmed") && isOwner ? (
                <button
                  type="button"
                  className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
                  onClick={() => {
                    setIsDeleteOpen(true);
                    setIsMoreOpen(false);
                  }}
                >
                  <Trash2Icon size={18} color="#E53935" />
                  {t("orderInfo.ordersInfoHeader.deleteOrderButton")}
                </button>
              ) : null} */}
            </div>
          </div>
        ) : null}
      </div>

      {isCancelOpen ? (
        <CancelApp
          item={item}
          onClose={() => setIsCancelOpen(false)}
          onUpdateData={onForceUpdate}
          isAirOrder
        />
      ) : null}
      {isDeleteOpen ? (
        <OrdersDelete
          item={item}
          onClose={() => setIsDeleteOpen(false)}
          onUpdateData={() => {
            onForceUpdate && onForceUpdate();
            setIsOpen && setIsOpen(false);
          }}
          isAirOrder
        />
      ) : null}
      {isReturnToTenderOpen ? (
        <ReturnToTenderModal
          item={item}
          onSubmit={Aviation.updateClientStatus}
          onClose={() => setIsReturnToTenderOpen(false)}
          onSuccess={() => onOrderDataChanged?.()}
        />
      ) : null}
      <ExtendTenderEndDate
        isOpen={isExtendTenderOpen}
        orderId={item?.id ? String(item.id) : ""}
        tenderDateEnd={item?.tenderDateEnd}
        assignmentMethod={item?.assignmentMethod}
        onSuccess={() => {
          setIsExtendTenderOpen(false);
          onForceUpdate && onForceUpdate();
        }}
        onCancel={() => setIsExtendTenderOpen(false)}
        isAvia
      />
      <AddProjectNumberModal
        isOpen={isProjectNumberOpen}
        orderId={item?.id}
        onSubmit={(orderId, referenceNumber) => Aviation.updateReferenceNumber(orderId, referenceNumber)}
        onSuccess={() => {
          setIsProjectNumberOpen(false);
          onForceUpdate && onForceUpdate();
        }}
        onCancel={() => setIsProjectNumberOpen(false)}
      />
      <RebidTenderModal
        isOpen={isRebidTenderOpen}
        orderId={item?.id}
        onSubmit={(orderId, tenderDateEnd) => Aviation.rebidTender(orderId, tenderDateEnd)}
        onSuccess={() => {
          setIsRebidTenderOpen(false);
          onOrderDataChanged?.();
        }}
        onCancel={() => setIsRebidTenderOpen(false)}
      />
      <EditAviationRouteDatesModal
        isOpen={isEditRouteDatesOpen}
        order={item}
        onSuccess={() => onForceUpdate?.()}
        onCancel={() => setIsEditRouteDatesOpen(false)}
      />
    </div>
  );
};

export default OrderInfoAviationHeader;
